import { from, Observable, Subject, throwError } from 'rxjs';
import { AjaxRequest, AjaxResponse } from 'rxjs/ajax';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { CoreEnvironment } from '../data/core-environment';
import { Http } from '../data/http';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import {
    RpcAzureOperation,
    RpcAzureOperationResult,
    RpcAzureOperationType,
    RpcAzureResponseKey
} from '../rpc/azure/rpc-azure-model';
import { RpcAzureRequestClient } from '../rpc/azure/rpc-azure-request-client';
import { Rpc } from '../rpc/rpc';
import { ConnectionProperties } from '../security/connection';
import { ArmTelemetry, AzureConstants, AzureIntegrationType } from './azure-constants';
import { AzureLogging } from './azure-logging';
import { AadAppConfig } from './models/aadAppConfig-data';
import { DeploymentOperationRequestBody } from './resources/deployment-operation-request-body';
import { DeploymentOperationResponse } from './resources/deployment-operation-response';
import { LocationsOperationResult } from './resources/location-data';
import {
    ResourceGroupOperationResult,
    ResourceGroupRequestBody,
    ResourceGroupsOperationResult
} from './resources/resource-group-data';
import { SubscriptionsOperationResult } from './resources/subscription-data';

/**
 * Azure Manager class. Handles detecting and configuring Azure on a set of servers.
 */
export class AzureManager {

    private watcher: Subject<RpcAzureOperationResult>;
    private azureLogging: AzureLogging;
    private readonly AzureGlobal = 'azureglobal';
    private readonly AzureChina = 'azurechina';
    private readonly AzureUSGov = 'azureusgov';

    /**
     * Initializes a new instance of the Azure Manager class
     * @param rpc The rpc to forward auth requests to a parent window
     */
    constructor(private rpc: Rpc, private http: Http) {
        this.watcher = new Subject<RpcAzureOperationResult>();
        this.azureLogging = new AzureLogging();
    }

    /**
     * Initialize the azure manager
     */
    public initialize() {
        // When in Shell (the top window) do not register. Only extension register.
        if (MsftSme.isExtension()) {
            this.rpc.register(RpcAzureResponseKey.command, this.onRpcResponse.bind(this));
        }
    }

    /**
     * Get application from gateway
     */
    public getAppInfo(): Observable<any> {
        return this.sendRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.GetAadStatus
        });
    }

    /**
     * Open the register Aad dialog
     */
    public openRegisterAadDialog(): Observable<any> {
        return this.sendRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.OpenAzureDialog
        });
    }

    /**
     * Signs in user.
     */
    public signIn(): Observable<any> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.SignIn
        });
    }

    /**
     * Signs out user.
     */
    public signOut(): Observable<any> {
        return this.sendRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.SignOut
        });
    }

    /**
     * Switches user accounts.
     */
    public switchAccounts(): Observable<any> {
        throw new Error('To be implemented');
    }

    /**
     * Get user token from azure
     */
    public getToken(): Observable<any> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.GetAadToken
        });
    }

    /**
     * Get the Azure AD graph token from azure
     */
    public getGraphToken(): Observable<any> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.GetAadGraphToken
        });
    }

    /**
     * Get the Microsoft graph token from azure
     * @param scopes The set of permissions for which the token has to be requested.
     * https://docs.microsoft.com/en-us/graph/permissions-reference
     * A null value will request default scopes - openid and profile.
     */
    public getMicrosoftGraphToken(scopes: string[] = null): Observable<any> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.GetMicrosoftGraphToken,
            tokenScopes: scopes
        });
    }

    /**
     * Checks to see if user is signed in. Returns true if signed in, else false.
     */
    public checkUserSignedIn(): Observable<boolean> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.CheckUserSignedIn
        }).pipe(map(result => {
            const isSignedIn = result && result.appInfo && result.appInfo.token;
            return !!isSignedIn;
        }));
    }

    /**
     * Get Azure subscriptions.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public getSubscriptions(integrationType?: AzureIntegrationType): Observable<SubscriptionsOperationResult> {
        let armEndpoint = null;
        const url = AzureConstants.azureSubscriptionUrl;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                return this.getRequestWithArmToken(url.format(armEndpoint), integrationType);
            }),
            map(result => {
                const response: SubscriptionsOperationResult = {
                    httpStatusCode: result.status,
                    value: result.response
                };
                return response;
            })
        );
    }

    /**
     * Check whether a resource group exists or not.
     * @param subscriptionId The subscription id.
     * @param resourceGroupName The resource group name.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public checkResourceGroupExists(
        subscriptionId: string,
        resourceGroupName: string,
        integrationType?: AzureIntegrationType): Observable<{ status: number }> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                return this.getArmToken();
            }),
            mergeMap(token => {
                const url = AzureConstants.resourceGroupUrl.format(armEndpoint, subscriptionId, resourceGroupName);
                const ajaxRequest = this.createRequestWithHeaders(token, { integrationType: integrationType });
                return this.http.head(url, ajaxRequest);
            }),
            map(result => {
                return { status: result.status };
            })
        );
    }

    /**
     * Create or update (if it exists) resource group.
     * @param subscriptionId The subscription id.
     * @param resourceGroupName The resource group name.
     * @param body request body. Optional. See definition of ResourceGroupRequestBody for what fields can be inside this body.
     * If this is not provided, a default of { \'location\': \'West US\' } will be used.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public createResourceGroup(
        subscriptionId: string,
        resourceGroupName: string,
        body?: ResourceGroupRequestBody,
        integrationType?: AzureIntegrationType): Observable<ResourceGroupOperationResult> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.resourceGroupUrl.format(armEndpoint, subscriptionId, resourceGroupName);
                if (!body) {
                    body = <ResourceGroupRequestBody>{ location: 'West US' };
                }
                return this.putRequestWithArmToken(url, body, integrationType);
            }),
            map(result => {
                const response: ResourceGroupOperationResult = {
                    httpStatusCode: result.status,
                    value: result.response
                };
                return response;
            })
        );
    }

    /**
     * List all resource groups in a subscription.
     * @param subscriptionId The subscription id.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public getResourceGroups(subscriptionId: string, integrationType?: AzureIntegrationType): Observable<ResourceGroupsOperationResult> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.listResourceGroups.format(armEndpoint, subscriptionId);
                return this.getRequestWithArmToken(url, integrationType);
            }),
            map(result => {
                const response: ResourceGroupsOperationResult = {
                    httpStatusCode: result.status,
                    value: result.response
                };
                return response;
            })
        );
    }

    /**
     * Delete resource group.
     * @param subscriptionId The subscription id.
     * @param resourceGroupName The resource group name.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public deleteResourceGroup(
        subscriptionId: string,
        resourceGroupName: string,
        integrationType?: AzureIntegrationType): Observable<{ status: number }> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.resourceGroupUrl.format(armEndpoint, subscriptionId, resourceGroupName);
                return this.deleteRequestWithArmToken(url, integrationType);
            }),
            map(result => {
                // result.status will either be 200 or 202.
                return { status: result.status };
            })
        );
    }

    /**
     * Get all available geo-locations.
     * @param subscriptionId The subscription id.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public getAllAzureLocations(subscriptionId: string, integrationType?: AzureIntegrationType): Observable<LocationsOperationResult> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.getAllGeoLocations.format(armEndpoint, subscriptionId);
                return this.getRequestWithArmToken(url, integrationType);
            }),
            map(result => {
                const response: LocationsOperationResult = {
                    httpStatusCode: result.status,
                    value: result.response
                };
                return response;
            })
        );
    }

    /**
     * Deploy resources with Resource Manager templates and Resource Manager REST API.
     * @param subscriptionId The subscription id.
     * @param resourceGroupName The resource group name.
     * @param deploymentName The deployment name.
     * @param resourceTemplate The resource template content.
     * @param templateParameters The resource template parameters.
     * @param deploymentMode The mode that is used to deploy resources.This value can be either Incremental or Complete.
     * In Incremental mode, resources are deployed without deleting existing resources that are not included in the template.
       In Complete mode, resources are deployed and existing resources in the resource group that are not included in the
       template are deleted.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public DeployAzureTemplate(
        subscriptionId: string,
        resourceGroupName: string,
        deploymentName: string,
        resourceTemplate: string,
        templateParameters: string,
        deploymentMode: string = 'Incremental',
        integrationType?: AzureIntegrationType): Observable<DeploymentOperationResponse> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.ArmDeploymentUrl.format(armEndpoint, subscriptionId, resourceGroupName, deploymentName);
                const body: DeploymentOperationRequestBody = {
                    properties: {
                        template: JSON.parse(resourceTemplate),
                        parameters: JSON.parse(templateParameters),
                        mode: deploymentMode
                    }
                };

                return this.putRequestWithArmToken(url, body, integrationType);
            }),
            tap(result => this.logTelemetry(result)),
            map(result => this.createDeploymentOperationResponse(result))
        );
    }

    /**
     * Validate resources with Resource Manager templates and Resource Manager REST API.
     * @param subscriptionId The subscription id.
     * @param resourceGroupName The resource group name.
     * @param deploymentName The deployment name.
     * @param resourceTemplate The resource template content.
     * @param templateParameters The resource template parameters.
     * @param deploymentMode The mode that is used to deploy resources.This value can be either Incremental or Complete.
     * In Incremental mode, resources are deployed without deleting existing resources that are not included in the template.
       In Complete mode, resources are deployed and existing resources in the resource group that are not included in the
       template are deleted.
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public validateAzureTemplate(
        subscriptionId: string,
        resourceGroupName: string,
        deploymentName: string,
        resourceTemplate: string,
        templateParameters: string,
        deploymentMode: string = 'Incremental',
        integrationType?: AzureIntegrationType
    ): Observable<DeploymentOperationResponse> {
        let armEndpoint = null;
        return this.getAppInfo().pipe(
            mergeMap(({ appInfo }: { appInfo: AadAppConfig }) => {
                if (appInfo) {
                    armEndpoint = this.getArmEndpoint(appInfo.cloudName);
                } else {
                    return throwError(() => new Error('Gateway is not registered to Azure.'));
                }

                const url = AzureConstants.validateAzureTemplate.format(armEndpoint, subscriptionId, resourceGroupName, deploymentName);
                const body: DeploymentOperationRequestBody = {
                    properties: {
                        template: JSON.parse(resourceTemplate),
                        parameters: JSON.parse(templateParameters),
                        mode: deploymentMode
                    }
                };

                return this.postRequestWithArmToken(url, body, integrationType);
            }),
            map(result => this.createDeploymentOperationResponse(result))
        );
    }

    /**
     * Creates azure request with headers
     * @param token bearer token for ARM request
     * @param options additional information that can be send in the headers of the request.
     *  - additionalHeaders any headers besides 'Authorization'
     *  - integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     *    Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     *    on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     *    explicitly set.
     * 'x-ms-client-request-id', 'x-ms-return-client-request-id', and 'Accept-Language'.
     * @return formatted AjaxRequest for an ARM call
     */
    public createRequestWithHeaders(
        token: string,
        options?: {
            additionalHeaders?: any,
            integrationType?: AzureIntegrationType,
            connectionProperties?: ConnectionProperties
        }): AjaxRequest {
        const ajaxRequest: AjaxRequest = <AjaxRequest>{ headers: {} };

        ajaxRequest.headers = (options && options.additionalHeaders) || {};
        (<any>ajaxRequest).headers[AzureConstants.authorizationHeaderKey] = AzureConstants.bearer + token;

        const integrationType = (options && options.integrationType) || ArmTelemetry.toIntegrationType(MsftSme.self().Init.moduleName);

        // TODO: Remove work around. Only intended for the quality release for Jan 2020.
        const connectionProperties = options && options.connectionProperties;
        (<any>ajaxRequest).headers[AzureConstants.clientRequestHeaderKey] =
            this.formatClientRequestId(
                integrationType,
                this.hexEncodeTelemetryData(connectionProperties));

        (<any>ajaxRequest).headers[AzureConstants.returnClientRequestHeaderKey] = 'True';
        (<any>ajaxRequest).headers[AzureConstants.acceptLanguageHeaderKey] = CoreEnvironment.localizationManager.getLocaleId().neutral;
        return ajaxRequest;
    }

    /**
     * Gets the arm token.
     */
    public getArmToken(): Observable<string> {
        return this.sendTokenRequest({
            requestId: MsftSme.newGuid(),
            operation: RpcAzureOperationType.GetAadToken
        })
            .pipe(
                map(result => {
                    return result && result.appInfo && result.appInfo.token;
                }));
    }

    // TODO: Remove work around. Only intended for the quality release for Jan 2020.
    /**
 * Parses connection properties for ARM telemetry data and encode in hex
 * @param connectionProperties properties on the current active connection
 * @return hex encoded json object.
 */
    public hexEncodeTelemetryData(connectionProperties?: ConnectionProperties): string {
        if (connectionProperties
            && connectionProperties['operatingSystem']
            && connectionProperties['version']
            && connectionProperties['computerModel']
            && connectionProperties['computerManufacturer']
            && connectionProperties['isS2dEnabled'] != null) {
            const telemetryData = {
                operatingSystem: connectionProperties['operatingSystem'],
                version: connectionProperties['version'],
                computerManufacturer: connectionProperties['computerManufacturer'],
                computerModel: connectionProperties['computerModel'],
                isS2dEnabled: connectionProperties['isS2dEnabled']
            };
            const stringifiedData = JSON.stringify(telemetryData);
            try {
                return MsftSme.toHex(stringifiedData);
            } catch {
                return null;
            }
        }
        return null;
    }

    // TODO: Remove work around. Only intended for the quality release for Jan 2020.
    private formatClientRequestId(integrationType: string, encodedData?: string): string {
        let clientRequestId = AzureConstants.clientRequestIdQualifier.format(MsftSme.newGuid());

        if (integrationType) {
            clientRequestId += AzureConstants.integrationTypeDataParameter.format(integrationType);
        }

        if (encodedData != null) {
            if (encodedData.length < AzureConstants.maxEncodedTelemetryDataLength) {
                clientRequestId += AzureConstants.telemetryDataParameter.format(encodedData);
            } else {
                clientRequestId += AzureConstants.telemetryDataParameter.format(AzureConstants.telemetryDataInvalidValue);
            }
        }

        // Ordering of parameters is important for parsing in telemetry pipeline.
        clientRequestId += AzureConstants.sourceParameter;

        return clientRequestId;
    }

    /**
     * Send request to RPC
     * @param request RpcAzureOperation
     */
    private sendRequest(request: RpcAzureOperation): Observable<any> {
        Logging.log({
            level: LogLevel.Debug,
            message: 'Sending request to AzureManagerShellService. Request:{0}'.format(JSON.stringify(request)),
            source: 'AzureManager'
        });

        return from(RpcAzureRequestClient.azureRequest(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter((result) => result.requestId === request.requestId),
                take(1),
                map((result) => result));
    }

    /**
     * Send token request to RPC
     * @param request RpcAzureOperation
     */
    private sendTokenRequest(request: RpcAzureOperation): Observable<any> {
        Logging.log({
            level: LogLevel.Debug,
            message: 'Sending request to AzureManagerShellService. Request:{0}'.format(JSON.stringify(request)),
            source: 'AzureManager'
        });

        return from(RpcAzureRequestClient.azureRequest(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter((result) => result.requestId === request.requestId),
                take(1),
                map((result) => {
                    if (result.appInfo) {
                        return result;
                    } else if (result.error) {
                        throw new Error(result.error);
                    }

                    throw new Error(MsftSme.getStrings<Strings>().MsftSmeShell.Core.Rpc.Azure.Unexpected.Error.message);
                }));
    }

    /**
     * Handles Rpc response.
     */
    private onRpcResponse(data: RpcAzureOperationResult): Promise<any> {
        this.watcher.next(data);
        return Promise.resolve();
    }

    /**
     * Determine cloud specific ARM endpoint based on the cloud that gateway is registered to.
     * @param cloudName cloudName from gateway's app registration.
     * @returns Returns cloud specific ARM endpoint.
     */
    public getArmEndpoint(cloudNameParam: string): string {
        const cloudName = MsftSme.replaceAll(cloudNameParam, ' ', '').toLowerCase();

        if (cloudName === this.AzureGlobal) {
            return AzureConstants.azureGlobalArmEndpoint;
        } else if (cloudName === this.AzureChina) {
            return AzureConstants.azureChinaArmEndpoint;
        } else if (cloudName === this.AzureUSGov) {
            return AzureConstants.azureUSGovArmEndpoint;
        }
    }

    /**
     * Make a GET request using ARM token.
     * @param url endpoint for request
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public getRequestWithArmToken(url: string, integrationType?: AzureIntegrationType): Observable<AjaxResponse<any>> {
        return this.getArmToken().pipe(
            mergeMap(token => {
                const ajaxRequest = this.createRequestWithHeaders(token, { integrationType: integrationType });
                return this.http.get(url, ajaxRequest);
            }));
    }

    /**
     * Make a POST request using ARM token.
     * @param url endpoint for request
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public postRequestWithArmToken(url: string, body: any, integrationType?: AzureIntegrationType): Observable<AjaxResponse<any>> {
        return this.getArmToken().pipe(
            mergeMap(token => {
                const ajaxRequest = this.createRequestWithHeaders(token, { integrationType: integrationType });
                return this.http.post(url, body, ajaxRequest);
            }));
    }

    /**
     * Make a PUT request using ARM token.
     * @param url endpoint for request
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public putRequestWithArmToken(url: string, body: any, integrationType?: AzureIntegrationType): Observable<AjaxResponse<any>> {
        if (!MsftSme.self().Init.isProduction &&
            (url.includes(AzureConstants.azureGlobalArmEndpoint) ||
             url.includes(AzureConstants.azureChinaArmEndpoint) ||
             url.includes(AzureConstants.azureUSGovArmEndpoint)) &&
            url.includes(AzureConstants.resourceDeploymentSubstring)) {
            Logging.logWarning('putRequestWithArmToken', 'Use DeployAzure Template to create resource.');
        }

        return this.getArmToken().pipe(
            mergeMap(token => {
                const ajaxRequest = this.createRequestWithHeaders(token, { integrationType: integrationType });
                return this.http.put(url, body, ajaxRequest);
            }));
    }

    /**
     * Make a DELETE request using ARM token.
     * @param url endpoint for request
     * @param integrationType integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
     * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
     * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
     * explicitly set.
     */
    public deleteRequestWithArmToken(url: string, integrationType?: AzureIntegrationType): Observable<AjaxResponse<any>> {
        return this.getArmToken().pipe(
            mergeMap(token => {
                const ajaxRequest = this.createRequestWithHeaders(token, { integrationType: integrationType });
                return this.http.delete(url, ajaxRequest);
            }));
    }

    public createDeploymentOperationResponse(result: AjaxResponse<any>): DeploymentOperationResponse {
        const headers = result.xhr.getAllResponseHeaders();
        const headerArray = headers && headers.trim().split(/[\r\n]+/);
        const headerMap: Map<string, string> = new Map<string, string>();
        headerArray.forEach((x: string) => {
            const parts = x.split(': ');
            const header = parts && parts.shift();
            const value = parts && parts.join(': ');
            headerMap.set(header, value);
        });

        const response: DeploymentOperationResponse = {
            httpStatusCode: result.status ? result.status : null,
            responseHeaders: headerMap ? headerMap : null,
            response: result.response ? result.response : null
        };

        return response;
    }

    public logTelemetry(response: any): void {
        response = response.response ? response.response : response;

        if (response && response.properties && response.properties.dependencies) {
            let dependentResourceIds = response.properties.dependencies;
            dependentResourceIds = dependentResourceIds.map(dependentResourceId => dependentResourceId.id);
            this.azureLogging.submitCreateResourceTelemetry(dependentResourceIds);
        } else {
            this.azureLogging.submitCreateResourceTelemetry();
        }
    }
}
